import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { TextFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import MobiusTextField from "@insite/mobius/TextField";
import StandardControl from "@insite/shell-public/Components/StandardControl";
import { EditorTemplateProps } from "@insite/shell-public/EditorTemplateProps";
import ButtonBar from "@insite/shell/Components/Modals/ButtonBar";
import { ChatMessage } from "@insite/shell/Services/ContentAdminService";
import { generateMetaKeywords } from "@insite/shell/Store/PageEditor/PageEditorActionCreators";
import ShellState from "@insite/shell/Store/ShellState";
import { TS } from "@insite/shell/TS";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";

const mapStateToProps = (state: ShellState) => {
    const currentPage = getCurrentPage(state);
    let pageId = currentPage.id;
    if (currentPage.type === "VariantRootPage") {
        const defaultVariant = state.pageTree.treeNodesByParentId[currentPage.nodeId]?.find(o => o.isDefaultVariant);
        pageId = defaultVariant ? defaultVariant.pageId : "";
    }

    return {
        pageUrl: `/Content/Page/${pageId}`,
        showMetadataGeneration:
            state.shellContext.settings?.settingsCollection?.websiteSettings.contentPagesMetadataGeneration &&
            !state.pageEditor.isEditingNewPage,
    };
};

const mapDispatchToProps = {
    generateMetaKeywords,
};

interface State {
    isModalOpen: boolean;
    isGenerating: boolean;
    isMetaDescription: boolean;
    generatedMetaData: string;
}

type Props = EditorTemplateProps<string, TextFieldDefinition> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

class MetaDataField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isModalOpen: false,
            isGenerating: false,
            isMetaDescription: this.props.fieldDefinition.name === "metaDescription",
            generatedMetaData: "",
        };
    }

    onChange = (event: React.FormEvent<HTMLInputElement>) => {
        const { fieldDefinition, updateField } = this.props;
        updateField(fieldDefinition.name, event.currentTarget.value);
    };

    generateHandler = () => {
        const { pageUrl, generateMetaKeywords } = this.props;
        this.setState({ isModalOpen: true, isGenerating: true });
        generateMetaKeywords(
            pageUrl,
            this.state.isMetaDescription,
            false,
            undefined,
            (response: ChatMessage[], errorMessage?: string) => {
                if (response?.length > 0) {
                    const message = response.find(x => x.role === "assistant");
                    this.setState({ generatedMetaData: message?.content ?? "" });
                } else if (errorMessage) {
                    this.setState({ generatedMetaData: `${TS("Error")}: ${errorMessage}` });
                }

                this.setState({ isGenerating: false });
            },
        );
    };

    regenerateHandler = () => {
        const { pageUrl, generateMetaKeywords } = this.props;
        this.setState({ isGenerating: true });
        generateMetaKeywords(
            pageUrl,
            this.state.isMetaDescription,
            true,
            this.state.generatedMetaData,
            (response: ChatMessage[], errorMessage?: string) => {
                if (response?.length > 0) {
                    const message = response.find(x => x.role === "assistant");
                    this.setState({ generatedMetaData: message?.content ?? "" });
                } else if (errorMessage) {
                    this.setState({ generatedMetaData: errorMessage });
                }

                this.setState({ isGenerating: false });
            },
        );
    };

    generatedMetaDataChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ generatedMetaData: event.target.value });
    };

    closeModalHandler = () => {
        this.setState({ isModalOpen: false, generatedMetaData: "" });
    };

    saveHandler = () => {
        const { fieldDefinition, updateField } = this.props;
        updateField(fieldDefinition.name, this.state.generatedMetaData);
        this.setState({ isModalOpen: false, generatedMetaData: "" });
    };

    render() {
        const {
            props: { fieldDefinition, fieldValue, item, showMetadataGeneration },
            state: { isModalOpen, isGenerating, isMetaDescription, generatedMetaData },
        } = this;
        return (
            <StandardControl fieldDefinition={fieldDefinition}>
                <MobiusTextField
                    id={fieldDefinition.name}
                    type="text"
                    value={fieldValue}
                    placeholder={fieldDefinition.placeholder}
                    maxLength={fieldDefinition.maxLength}
                    onChange={this.onChange}
                    disabled={fieldDefinition.isEnabled && !fieldDefinition.isEnabled(item)}
                />
                {showMetadataGeneration && (
                    <>
                        <ButtonWrapper>
                            <Button onClick={this.generateHandler} variant="secondary">
                                {TS(fieldValue ? "Regenerate" : "Generate")}
                            </Button>
                        </ButtonWrapper>
                        <Modal
                            isOpen={isModalOpen}
                            headline={TS(isMetaDescription ? "Generated Meta Description" : "Generated Meta Keywords")}
                            handleClose={this.closeModalHandler}
                            closeOnEsc
                            closeOnScrimClick
                        >
                            {isGenerating && (
                                <LoadingSpinnerWrapper>
                                    <LoadingSpinner />
                                </LoadingSpinnerWrapper>
                            )}
                            {!isGenerating && (
                                <TextAreaStyle
                                    value={generatedMetaData}
                                    onChange={this.generatedMetaDataChangeHandler}
                                />
                            )}
                            <ButtonBar>
                                <Button variant="secondary" onClick={this.closeModalHandler}>
                                    {TS("Close")}
                                </Button>
                                <Button variant="secondary" onClick={this.regenerateHandler}>
                                    {TS("Regenerate")}
                                </Button>
                                <Button variant="primary" onClick={this.saveHandler}>
                                    {TS("Save")}
                                </Button>
                            </ButtonBar>
                        </Modal>
                    </>
                )}
            </StandardControl>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MetaDataField);

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: end;
    margin-top: 10px;
`;

const TextAreaStyle = styled.textarea`
    width: 100%;
    height: 200px;
`;

const LoadingSpinnerWrapper = styled.div`
    text-align: center;
    padding-top: 80px;
    height: 200px;
`;
